import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, List, Plus } from "lucide-react";
import { ErrorBanner } from "../components/AuthShell.js";
import { CreateWithAiModal } from "../components/CreateWithAiModal.js";
import { Button } from "../components/ui/Button.js";
import { CARD_GRID_CLS, CardLink } from "../components/ui/Card.js";
import { EmptyState } from "../components/ui/EmptyState.js";
import { KindChip } from "../components/ui/KindChip.js";
import { PageHeader } from "../components/ui/PageHeader.js";
import { SearchInput } from "../components/ui/SearchInput.js";
import { SkeletonCardGrid } from "../components/ui/SkeletonCard.js";
import { useAuth } from "../context/AuthContext.js";
import { ApiError, fetchLists, type ApiListSummary } from "../lib/api.js";

function ListCard({ list }: { list: ApiListSummary }) {
  const preview = list.preview;
  const itemCount = list.itemCount;
  return (
    <li>
      <CardLink to={`/lists/${list.id}`}>
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-medium text-ink">{list.name}</h2>
          <div className="flex shrink-0 items-center gap-1.5">
            {list.isPublished ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-700">
                <Globe size={14} aria-hidden="true" />
                Published
              </span>
            ) : null}
            <KindChip kind={list.kind} />
          </div>
        </div>
        {list.description ? (
          <p className="mt-1 line-clamp-2 text-sm text-muted">{list.description}</p>
        ) : null}
        {preview.length > 0 ? (
          <ul className="mt-2 space-y-0.5 text-sm text-muted">
            {preview.map((text, i) => (
              <li key={`${i}-${text}`} className="truncate">
                · {text}
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mt-3 text-xs tabular-nums text-muted-sm">
          {itemCount} item{itemCount === 1 ? "" : "s"}
          {list.tags.length ? ` · ${list.tags.join(", ")}` : ""}
          {(list.memberCount ?? 1) >= 2 ? ` · ${list.memberCount} members` : ""}
        </p>
      </CardLink>
    </li>
  );
}

export function MyListsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const mySub = user?.sub ?? "";
  const [lists, setLists] = useState<ApiListSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [aiOpen, setAiOpen] = useState(false);

  async function reloadLists() {
    const data = await fetchLists();
    setLists(data);
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchLists();
        if (!cancelled) setLists(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof ApiError ? err.message : "Failed to load lists");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return lists;
    return lists.filter((l) => {
      if (l.name.toLowerCase().includes(q)) return true;
      if (l.description?.toLowerCase().includes(q)) return true;
      if (l.tags.some((t) => t.includes(q))) return true;
      return false;
    });
  }, [lists, filter]);

  const own = useMemo(() => filtered.filter((l) => l.ownerId === mySub), [filtered, mySub]);
  const shared = useMemo(() => filtered.filter((l) => l.ownerId !== mySub), [filtered, mySub]);
  const hasAnyShared = useMemo(() => lists.some((l) => l.ownerId !== mySub), [lists, mySub]);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:py-10">
      <PageHeader
        title="My Lists"
        subtitle="Reusable item sets — the building blocks you drop into templates. Not checkable on their own."
        actions={
          <>
            <Button type="button" variant="secondary" onClick={() => setAiOpen(true)}>
              Create with AI
            </Button>
            <Button
              type="button"
              icon={<Plus size={16} aria-hidden="true" />}
              onClick={() => navigate("/lists/new")}
            >
              New list
            </Button>
          </>
        }
      />

      <ErrorBanner message={error} />

      {!loading && lists.length > 0 ? (
        <div className="mt-4">
          <SearchInput
            id="listFilter"
            aria-label="Search lists"
            placeholder="Filter by name, description, or tag…"
            value={filter}
            onChange={setFilter}
          />
        </div>
      ) : null}

      {loading ? (
        <SkeletonCardGrid />
      ) : lists.length === 0 ? (
        <EmptyState
          icon={<List size={28} className="text-muted" aria-hidden="true" />}
          title="No lists yet"
          body="Lists are reusable building blocks you drop into templates — they aren't checkable on their own. Most people don't need these until they're reusing a lot."
          primary={
            <Button type="button" onClick={() => navigate("/lists/new")}>
              New list
            </Button>
          }
          secondary={
            <Button type="button" variant="secondary" onClick={() => setAiOpen(true)}>
              Create with AI
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <p className="mt-8 text-sm text-muted">No lists match &ldquo;{filter.trim()}&rdquo;.</p>
      ) : (
        <div className="mt-6 space-y-8">
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
              Owned by me
            </h2>
            {own.length === 0 ? (
              <p className="mt-3 text-sm text-muted">No owned lists in this filter.</p>
            ) : (
              <ul className={`mt-3 ${CARD_GRID_CLS}`}>
                {own.map((list) => (
                  <ListCard key={list.id} list={list} />
                ))}
              </ul>
            )}
          </section>

          {hasAnyShared ? (
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
                Shared with me
              </h2>
              {shared.length === 0 ? (
                <p className="mt-3 text-sm text-muted">
                  No shared lists match &ldquo;{filter.trim()}&rdquo;.
                </p>
              ) : (
                <ul className={`mt-3 ${CARD_GRID_CLS}`}>
                  {shared.map((list) => (
                    <ListCard key={list.id} list={list} />
                  ))}
                </ul>
              )}
            </section>
          ) : null}
        </div>
      )}

      <CreateWithAiModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onSaved={() => {
          void reloadLists().catch(() => {});
        }}
      />
    </div>
  );
}
